import type { FC } from 'react';
import React, { useContext, useCallback, useEffect } from 'react';
import type { WrappedComponentProps } from 'react-intl-next';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl-next';

import Tooltip from '@atlaskit/tooltip/Tooltip';
import ShareIcon from '@atlaskit/icon/core/migration/share';
import type { ButtonProps } from '@atlaskit/button/standard-button';

import { ShareButtonLiteLoader } from '@confluence/share';
import { useSessionData } from '@confluence/session-data';

import { FeedCardContext } from '../FeedCardContext';

import { StyledActionButton, ActionButtonWrapper } from './ActionButtonStyles';
import type { FollowingFeedSortByValue, PopularFeedTimeGranularityValue } from '../../../Feed';

const i18n = defineMessages({
	share: {
		id: 'feed.card.share',
		defaultMessage: 'Share',
		description: 'Text to be displayed on the share button in the card',
	},
});

type TriggerProps = {
	buttonProps: ButtonProps;
	setIsMenuOpen: (open: boolean) => void;
};
const ShareTriggerComponent: FC<WrappedComponentProps & TriggerProps> = ({
	intl,
	buttonProps,
	setIsMenuOpen,
}) => {
	const { isSelected } = buttonProps;
	useEffect(() => {
		setIsMenuOpen(!!isSelected);
	}, [setIsMenuOpen, isSelected]);

	return (
		<Tooltip content={<FormattedMessage {...i18n.share} />} position="top" hideTooltipOnClick>
			<StyledActionButton
				{...buttonProps}
				appearance="subtle"
				iconBefore={<ShareIcon label={intl.formatMessage(i18n.share)} />}
			/>
		</Tooltip>
	);
};
const ShareTrigger = injectIntl(ShareTriggerComponent);

type Props = {
	contentId: string;
	contentUrl: string;
	contentTitle: string;
	contentType: 'page' | 'blogpost';
	contentSubType?: string | null;
	setIsMenuOpen: (open: boolean) => void;
	popularFeedTimeGranularity?: PopularFeedTimeGranularityValue;
	followingFeedSortby?: FollowingFeedSortByValue;
};
export const FeedCardShareAction: FC<Props> = ({
	contentId,
	contentUrl,
	contentTitle,
	contentType,
	contentSubType,
	setIsMenuOpen,
	popularFeedTimeGranularity,
	followingFeedSortby,
}) => {
	const { fireAnalyticsEvent } = useContext(FeedCardContext);
	const { userId: currentUserId } = useSessionData();

	const customShareButton = useCallback(
		(mainProps: any, triggerProps: any) => {
			const { onClick } = mainProps;
			if (onClick) {
				mainProps.onClick = (...params) => {
					onClick(...params);
					fireAnalyticsEvent({
						type: 'sendUIEvent',
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'share',
						additionalAttributes: {
							popularFeedTimeGranularity,
							followingFeedSortby,
						},
					});
				};
			}
			return (
				<ShareTrigger
					buttonProps={{ ...mainProps, ...triggerProps }}
					setIsMenuOpen={setIsMenuOpen}
				/>
			);
		},
		[setIsMenuOpen, fireAnalyticsEvent, popularFeedTimeGranularity, followingFeedSortby],
	);

	return (
		<ActionButtonWrapper>
			<ShareButtonLiteLoader
				contentId={contentId}
				contentType={contentType}
				contentSubType={contentSubType}
				title={contentTitle}
				userId={currentUserId || ''}
				link={`${window.location.origin}${contentUrl}`}
				renderCustomTriggerButton={customShareButton}
			/>
		</ActionButtonWrapper>
	);
};
